import React, { useMemo, useState } from "react";
import {
  CalendarDays,
  CloudSun,
  Clock3,
  Globe2,
  LayoutGrid,
  ListTodo,
  Monitor,
  Newspaper,
  Search,
  Settings2,
  SlidersHorizontal,
  WalletCards,
} from "lucide-react";
import { Badge } from "../Components/ui/badge";
import { Button } from "../Components/ui/button";
import { Card, CardContent } from "../Components/ui/card";
import { useLanguage } from "../contexts/LanguageContext";

const categories = [
  { label: "All", icon: LayoutGrid },
  { label: "Calendar", icon: CalendarDays },
  { label: "Weather", icon: CloudSun },
  { label: "World Clock", icon: Globe2 },
  { label: "Tasks", icon: ListTodo },
  { label: "News", icon: Newspaper },
  { label: "Finance", icon: WalletCards },
];

const templates = [
  {
    id: "monthly-calendar",
    name: "Monthly Calendar",
    category: "Calendar",
    device: "Standard",
    image: "/images/templates/kylendar-verified-standard-month-calendar.png",
    componentKey: "month_calendar",
    componentSize: 0,
    canvas: { width: 800, height: 480 },
    source: "Kylendar /Help-picture/en/Month Calendar.png",
    description: "A real Month Calendar card that uses the full Standard canvas.",
    includedWidgets: ["Month Calendar", "Month grid", "Current day"],
    layout: "Single component",
    label: "Verified",
  },
  {
    id: "daily-agenda",
    name: "Daily Agenda",
    category: "Tasks",
    device: "Standard",
    image: "/images/templates/kylendar-verified-standard-daily-agenda.png",
    componentKey: "today_schedule",
    componentSize: 0,
    canvas: { width: 800, height: 480 },
    source: "Kylendar /Help-picture/en/Today Schedule.png",
    description: "A real Today Schedule card for the appointments that shape your day.",
    includedWidgets: ["Today Schedule", "4 event slots"],
    layout: "Single component",
    label: "Verified",
  },
  {
    id: "weekly-timeline",
    name: "Weekly Timeline",
    category: "Calendar",
    device: "Standard",
    image: "/images/templates/kylendar-verified-standard-week-schedule.png",
    componentKey: "week_schedule",
    componentSize: 0,
    canvas: { width: 800, height: 480 },
    source: "Kylendar /Help-picture/en/Week Schedule.png",
    description: "A real Week Schedule card for seeing the whole week on one screen.",
    includedWidgets: ["Week Schedule", "7 day columns"],
    layout: "Single component",
    label: "Verified",
  },
  {
    id: "world-clock",
    name: "Global Clock",
    category: "World Clock",
    device: "Mini",
    image: "/images/templates/kylendar-mini-world-clock.png",
    componentKey: "time",
    componentSize: 6,
    canvas: { width: 400, height: 300 },
    source: "Kylendar Templates42 Card42Preview (4.2)",
    description: "A real 4.2 World Clock card with the device canvas proportions intact.",
    includedWidgets: ["World Clock", "Date", "UTC offset"],
    layout: "Single component",
    label: "Verified",
  },
  {
    id: "weather-today",
    name: "Weather Today",
    category: "Weather",
    device: "Mini",
    image: "/images/templates/kylendar-mini-weather-today.png",
    componentKey: "weather_today",
    componentSize: 6,
    canvas: { width: 400, height: 300 },
    source: "Kylendar Templates42 Card42Preview (4.2)",
    description: "A real 4.2 Weather Today card with the full 400×300 canvas.",
    includedWidgets: ["Weather", "4-slot forecast", "Timeline"],
    layout: "Single component",
    label: "Verified",
  },
  {
    id: "weekly-weather",
    name: "Weekly Weather",
    category: "Weather",
    device: "Mini",
    image: "/images/templates/kylendar-mini-weekly-weather.png",
    componentKey: "weekly_weather",
    componentSize: 6,
    canvas: { width: 400, height: 300 },
    source: "Kylendar Templates42 Card42Preview (4.2)",
    description: "A real 4.2 Weekly Weather card for current conditions and the week ahead.",
    includedWidgets: ["Current conditions", "6-day trend", "Date"],
    layout: "Single component",
    label: "Verified",
  },
  {
    id: "stock-info",
    name: "Stock Info",
    category: "Finance",
    device: "Standard",
    image: "/images/templates/kylendar-verified-standard-stock-info.png",
    componentKey: "stock_info_cards",
    componentSize: 0,
    canvas: { width: 800, height: 480 },
    source: "Kylendar /Help-picture/en/Stock Info.png",
    description: "A real Stock Info card with six watchlist slots across the Standard canvas.",
    includedWidgets: ["Stock Info", "6 symbols", "Daily change"],
    layout: "Single component",
    label: "Verified",
  },
  {
    id: "news-card",
    name: "News Card",
    category: "News",
    device: "Standard",
    image: "/images/templates/kylendar-verified-standard-news.png",
    componentKey: "news",
    componentSize: 0,
    canvas: { width: 800, height: 480 },
    source: "Kylendar /Help-picture/en/News Card.png",
    description: "A real News Card with the project’s title, source, and body layout.",
    includedWidgets: ["News Card", "Headline", "Article body"],
    layout: "Single component",
    label: "Verified",
  },
  {
    id: "daily-focus",
    name: "Daily Focus Card",
    category: "Tasks",
    device: "Mini",
    image: "/images/templates/kylendar-mini-daily-focus.png",
    componentKey: "daily_focus",
    componentSize: 6,
    canvas: { width: 400, height: 300 },
    source: "Kylendar Templates42 Card42Preview (4.2)",
    description: "A real 4.2 Daily Focus Card for the next event, focus tasks, and weather.",
    includedWidgets: ["Daily Focus", "Next event", "Focus tasks"],
    layout: "Single component",
    label: "Verified",
  },
  {
    id: "current-weather",
    name: "Current Weather",
    category: "Weather",
    device: "Mini",
    image: "/images/templates/kylendar-mini-current-weather.png",
    componentKey: "current_weather",
    componentSize: 6,
    canvas: { width: 400, height: 300 },
    source: "Kylendar Templates42 Card42Preview (4.2)",
    description: "A real 4.2 Current Weather card with the current reading and range.",
    includedWidgets: ["Current temperature", "Condition", "Range"],
    layout: "Single component",
    label: "Verified",
  },
];

const heroTemplateIds = ["monthly-calendar", "daily-agenda", "stock-info", "news-card", "world-clock", "weather-today"];
const heroTemplates = heroTemplateIds.map((id) => templates.find((template) => template.id === id)).filter(Boolean);
const popularTemplateIds = ["monthly-calendar", "daily-agenda", "world-clock", "weather-today", "stock-info"];
const popularTemplates = popularTemplateIds.map((id) => templates.find((template) => template.id === id)).filter(Boolean);

const widgets = [
  { name: "Daily Agenda", icon: ListTodo, templateId: "daily-agenda" },
  { name: "Stock Info", icon: WalletCards, templateId: "stock-info" },
  { name: "World Clock", icon: Clock3, templateId: "world-clock" },
  { name: "Weather Today", icon: CloudSun, templateId: "weather-today" },
  { name: "News Card", icon: Newspaper, templateId: "news-card" },
];

const setupCards = [
  { name: "Desk planning", detail: "Daily Agenda", templateId: "daily-agenda" },
  { name: "Market watch", detail: "Stock Info", templateId: "stock-info" },
  { name: "Weather check", detail: "Weather Today", templateId: "weather-today" },
  { name: "Focus routine", detail: "Daily Focus Card", templateId: "daily-focus" },
  { name: "Nightstand", detail: "Current Weather", templateId: "current-weather" },
];

export default function Templates() {
  const { t } = useLanguage();
  const copy = t("templates");
  const [category, setCategory] = useState("All");
  const [device, setDevice] = useState("All");

  const filteredTemplates = useMemo(() => {
    return templates.filter((template) => {
      const matchesCategory = category === "All" || template.category === category;
      const matchesDevice = device === "All" || template.device === device;

      return matchesCategory && matchesDevice;
    });
  }, [category, device]);

  const hasActiveFilters = category !== "All" || device !== "All";
  const templatesToShow = hasActiveFilters ? filteredTemplates : popularTemplates;
  const featuredTemplate = popularTemplates[0];

  function clearFilters() {
    setCategory("All");
    setDevice("All");
  }

  return (
    <main className="overflow-hidden bg-[#fbfffc] text-slate-950">
      <section className="relative border-b border-brand-100 bg-white px-5 pb-14 pt-12 sm:px-8 sm:pb-20 sm:pt-16 lg:px-10 lg:pb-24 lg:pt-20">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-[radial-gradient(ellipse_at_top_left,_rgba(211,250,229,.72),_transparent_62%)]" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[.82fr_1.18fr] lg:gap-20">
          <div className="max-w-xl">
            <Badge variant="green-outline" className="border-0 bg-brand-50 px-0 text-[10px] uppercase tracking-[0.2em]">{copy.eyebrow}</Badge>
            <h1 className="mt-5 max-w-lg font-display text-5xl font-extrabold leading-[.98] tracking-[-.065em] sm:text-6xl lg:text-[4.35rem]">{copy.title}</h1>
            <p className="mt-6 max-w-lg text-base leading-7 text-slate-600">{copy.body}</p>
          </div>
          <HeroTemplateMosaic />
        </div>
      </section>

      <section id="template-library" className="scroll-mt-20 px-5 py-12 sm:px-8 sm:py-16 lg:px-10 lg:py-20">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-2xl border border-brand-100 bg-white p-4 shadow-[0_18px_45px_-34px_rgba(8,122,88,.45)] sm:p-5">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700"><SlidersHorizontal className="h-4 w-4" aria-hidden="true" /></span>
                <div><p className="text-xs font-bold uppercase tracking-[.14em] text-slate-500">{copy.findEyebrow}</p><h2 className="mt-0.5 font-display text-base font-bold tracking-[-.02em]">{copy.findTitle}</h2></div>
              </div>
              <div className="flex flex-wrap gap-1.5 lg:justify-end">
                {categories.map(({ label, icon: Icon }, index) => <button key={label} type="button" aria-pressed={category === label} onClick={() => setCategory(label)} className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold transition focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1 ${category === label ? "border-brand-200 bg-brand-50 text-brand-800" : "border-slate-200 bg-white text-slate-600 hover:border-brand-200 hover:bg-brand-50/60 hover:text-brand-800"}`}><Icon className="h-3.5 w-3.5" aria-hidden="true" />{copy.categories[index]}</button>)}
              </div>
            </div>
            <div className="mt-5 flex flex-col gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2"><Monitor className="h-4 w-4 text-brand-700" aria-hidden="true" /><p className="text-xs font-bold uppercase tracking-[.14em] text-slate-500">{copy.optimized}</p></div>
              <div className="flex flex-wrap gap-2">
                {["All", "Mini", "Standard"].map((item) => <button key={item} type="button" aria-label={item === "All" ? copy.devices.all : item} aria-pressed={device === item} onClick={() => setDevice(item)} className={`rounded-lg border px-4 py-2 text-xs font-semibold transition focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1 ${device === item ? "border-brand-200 bg-brand-50 text-brand-800" : "border-slate-200 bg-white text-slate-600 hover:border-brand-200 hover:bg-brand-50/60 hover:text-brand-800"}`}>{item === "All" ? copy.devices.all : item === "Mini" ? copy.devices.mini : copy.devices.standard}</button>)}
              </div>
            </div>
          </div>

          <div className="mt-14 flex items-end justify-between gap-4">
            <div><p className="text-[10px] font-bold uppercase tracking-[.2em] text-brand-700">{copy.starting}</p><h2 className="mt-2 font-display text-3xl font-bold tracking-[-.05em] sm:text-4xl">{copy.popular}</h2><p className="mt-2 text-sm text-slate-500">{copy.popularBody}</p></div>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-500" aria-live="polite"><span>{templatesToShow.length}{hasActiveFilters ? ` ${templatesToShow.length === 1 ? copy.templateReady : copy.templatesReadyPlural}` : ` ${copy.templatesReady}`}</span>{hasActiveFilters && <span className="rounded-full bg-brand-50 px-2.5 py-1 text-[11px] font-semibold text-brand-800">{copy.filtersActive}</span>}</div>

          {templatesToShow.length > 0 ? <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">{templatesToShow.slice(0, 5).map((template) => <TemplateCard key={template.id} template={template} />)}</div> : <EmptyState hasActiveFilters={hasActiveFilters} onReset={clearFilters} />}

          <section id="widget-library" className="mt-20 scroll-mt-20 border-t border-slate-200/80 pt-12 sm:pt-16">
            <div className="flex items-end justify-between gap-4"><div><p className="text-[10px] font-bold uppercase tracking-[.2em] text-brand-700">{copy.widgetEyebrow}</p><h2 className="mt-2 font-display text-3xl font-bold tracking-[-.05em] sm:text-4xl">{copy.widgetTitle}</h2><p className="mt-3 max-w-xl text-sm leading-6 text-slate-600">{copy.widgetBody}</p></div></div>
            <div className="mt-7 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">{widgets.map((widget) => <WidgetCard key={widget.name} widget={widget} />)}</div>
          </section>
        </div>
      </section>

      <section id="template-preview" className="border-y border-brand-100 bg-[#f6fcf8] px-5 py-12 sm:px-8 sm:py-16 lg:px-10 lg:py-20">
        <div className="mx-auto max-w-7xl">
          <div className="flex items-end justify-between gap-4"><div><p className="text-[10px] font-bold uppercase tracking-[.2em] text-brand-700">{copy.detailEyebrow}</p><h2 className="mt-2 font-display text-3xl font-bold tracking-[-.05em] sm:text-4xl">{copy.detailTitle}</h2></div><Badge variant="default" className="hidden sm:inline-flex">{copy.verifiedPreview}</Badge></div>
          <div className="mt-8 grid gap-6 rounded-2xl border border-brand-100 bg-white p-5 shadow-[0_22px_60px_-44px_rgba(8,122,88,.65)] sm:p-7 lg:grid-cols-[.72fr_1.08fr_.9fr] lg:items-center">
            <div><p className="text-xs font-bold uppercase tracking-[.14em] text-brand-700">{getCategoryCopy(featuredTemplate.category, copy)} / {featuredTemplate.device}</p><p className="mt-3 font-display text-2xl font-bold tracking-[-.045em]">{getTemplateCopy(featuredTemplate, copy).name}</p><p className="mt-3 text-sm leading-6 text-slate-600">{getTemplateCopy(featuredTemplate, copy).description}</p></div>
            <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-inner sm:p-5"><TemplateArtwork template={featuredTemplate} large /></div>
            <div className="rounded-xl border border-slate-200 bg-white p-5"><div className="flex items-center justify-between"><p className="text-xs font-bold uppercase tracking-[.14em] text-slate-500">{copy.verifiedComponent}</p><Settings2 className="h-4 w-4 text-brand-700" aria-hidden="true" /></div><dl className="mt-5 space-y-4 text-sm"><DetailRow label={copy.componentKey} value={featuredTemplate.componentKey} /><DetailRow label={copy.canvasSize} value={formatCanvas(featuredTemplate)} /></dl><p className="mt-5 border-t border-slate-100 pt-4 text-[11px] leading-5 text-slate-500">{copy.rendered}</p></div>
          </div>
        </div>
      </section>

      <section className="px-5 py-12 sm:px-8 sm:py-16 lg:px-10 lg:py-20">
        <div className="mx-auto grid max-w-7xl gap-6 overflow-hidden rounded-2xl border border-brand-100 bg-white p-5 sm:p-8 lg:grid-cols-[.82fr_1.18fr] lg:items-center lg:p-10">
          <div><Badge variant="green-outline" className="border-brand-100 bg-white">{copy.realComponents}</Badge><h2 className="mt-4 max-w-md font-display text-3xl font-bold tracking-[-.05em] sm:text-4xl">{copy.customize}</h2><p className="mt-4 max-w-md text-sm leading-6 text-slate-600">{copy.customizeBody}</p></div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">{["daily-focus", "stock-info", "weather-today", "world-clock"].map((templateId) => <ComponentArrangementPreview key={templateId} template={templates.find((template) => template.id === templateId)} />)}</div>
        </div>
      </section>

      <section className="border-t border-slate-200/80 bg-white px-5 py-12 sm:px-8 sm:py-16 lg:px-10 lg:py-20">
        <div className="mx-auto max-w-7xl"><div><p className="text-[10px] font-bold uppercase tracking-[.2em] text-brand-700">{copy.setupEyebrow}</p><h2 className="mt-2 font-display text-3xl font-bold tracking-[-.05em] sm:text-4xl">{copy.setupTitle}</h2></div><p className="mt-3 max-w-xl text-sm leading-6 text-slate-600">{copy.setupBody}</p><div className="mt-7 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">{setupCards.map((setup) => <SetupCard key={setup.name} setup={setup} />)}</div></div>
      </section>

    </main>
  );
}

function HeroTemplateMosaic() {
  const { t } = useLanguage();
  const copy = t("templates");

  return (
    <div className="rounded-[1.75rem] border border-brand-100 bg-[#f7fbf7] p-3 shadow-[0_30px_80px_-55px_rgba(8,122,88,.75)] sm:p-5">
      <div className="mb-4 flex items-start justify-between gap-4 px-1">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[.16em] text-brand-700">{copy.verifiedComponent}</p>
          <p className="mt-1 max-w-xs text-xs leading-5 text-slate-500">{copy.rendered}</p>
        </div>
        <span className="shrink-0 rounded-full border border-brand-200 bg-white px-2.5 py-1 text-[10px] font-bold text-brand-700">4.2 + 7.5</span>
      </div>
      <div className="grid grid-cols-2 items-start gap-3 sm:grid-cols-3 sm:gap-4">
        {heroTemplates.map((template) => (
          <div key={template.id} className="self-start overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_12px_28px_-24px_rgba(8,122,88,.65)]">
            <TemplateArtwork template={template} />
            <div className="border-t border-slate-100 px-2.5 py-2.5">
              <p className="truncate text-[10px] font-bold text-slate-800">{getTemplateCopy(template, copy).name}</p>
              <p className="mt-1 text-[9px] font-semibold text-slate-400">{template.device} · {template.canvas.width}×{template.canvas.height}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TemplateCard({ template }) {
  const { t } = useLanguage();
  const copy = t("templates");
  const templateCopy = getTemplateCopy(template, copy);

  return (
    <Card className="group overflow-hidden rounded-xl border-slate-200 bg-white shadow-[0_12px_28px_-25px_rgba(8,122,88,.8)] transition hover:-translate-y-1 hover:border-brand-200 hover:shadow-[0_20px_32px_-24px_rgba(8,122,88,.55)]">
      <div className="relative bg-white p-2.5">
        <TemplateArtwork template={template} />
      </div>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2"><h3 className="font-display text-sm font-bold tracking-[-.025em]">{templateCopy.name}</h3><span className="shrink-0 text-[10px] font-semibold text-slate-400">{template.device}</span></div>
        <p className="mt-2 min-h-[42px] text-xs leading-5 text-slate-500">{templateCopy.description}</p>
        <p className="mt-3 text-[10px] font-semibold text-brand-700">{template.componentKey} · {formatCanvas(template)}</p>
      </CardContent>
    </Card>
  );
}

function WidgetCard({ widget }) {
  const { t } = useLanguage();
  const copy = t("templates");
  const Icon = widget.icon;
  const template = templates.find((item) => item.id === widget.templateId);
  const templateCopy = getTemplateCopy(template, copy);

  return (
    <Card className="group overflow-hidden rounded-xl border-slate-200 bg-white transition hover:-translate-y-1 hover:border-brand-200 hover:shadow-[0_18px_30px_-24px_rgba(8,122,88,.6)]">
      <div className="bg-[#f7fbf7] p-3"><TemplateArtwork template={template} /></div>
      <CardContent className="p-4"><div className="flex items-center gap-2"><span className="flex h-7 w-7 items-center justify-center rounded-md bg-brand-50 text-brand-700"><Icon className="h-3.5 w-3.5" aria-hidden="true" /></span><h3 className="text-sm font-bold">{templateCopy.name}</h3></div><p className="mt-2 text-xs text-slate-500">{template.componentKey} · {formatCanvas(template)}</p></CardContent>
    </Card>
  );
}

function SetupCard({ setup }) {
  const { t } = useLanguage();
  const copy = t("templates");
  const template = templates.find((item) => item.id === setup.templateId);
  const setupIndex = setupCards.findIndex((item) => item.name === setup.name);
  const setupCopy = copy.setup[setupIndex] || [setup.name, setup.detail];
  const templateCopy = getTemplateCopy(template, copy);

  return (
    <article className="group overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_14px_30px_-28px_rgba(8,122,88,.65)] transition hover:-translate-y-1 hover:border-brand-200">
      <div className="bg-[#f7fbf7] p-3"><TemplateArtwork template={template} /></div>
      <div className="p-3.5"><p className="text-xs font-bold">{setupCopy[0]}</p><p className="mt-1 text-[10px] text-slate-500">{templateCopy.name} <span className="text-slate-300">·</span> {copy.verifiedStarting}</p></div>
    </article>
  );
}

function DetailRow({ label, value }) {
  return <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3 last:border-0 last:pb-0"><dt className="text-xs text-slate-500">{label}</dt><dd className="text-right text-xs font-bold text-slate-800">{value}</dd></div>;
}

function EmptyState({ hasActiveFilters, onReset }) {
  const { t } = useLanguage();
  const copy = t("templates");
  return <div className="mt-6 rounded-xl border border-dashed border-brand-200 bg-white p-12 text-center"><Search className="mx-auto h-6 w-6 text-brand-700" aria-hidden="true" /><h3 className="mt-4 font-display font-bold">{copy.emptyTitle}</h3><p className="mt-2 text-sm text-slate-600">{copy.emptyBody}</p>{hasActiveFilters && <Button type="button" variant="green-outline" size="sm" onClick={onReset} className="mt-5">{copy.clear}</Button>}</div>;
}

function ComponentArrangementPreview({ template }) {
  const { t } = useLanguage();
  const copy = t("templates");
  if (!template) return null;
  const templateCopy = getTemplateCopy(template, copy);

  return <div className="overflow-hidden rounded-xl border border-brand-100 bg-white shadow-[0_10px_24px_-20px_rgba(8,122,88,.6)]"><div className="bg-[#f7fbf7] p-2"><TemplateArtwork template={template} /></div><div className="border-t border-slate-100 px-2.5 py-2"><p className="truncate text-[10px] font-bold text-slate-800">{templateCopy.name}</p><p className="mt-1 text-[9px] font-semibold text-slate-400">{template.componentKey} · {formatCanvas(template)}</p></div></div>;
}

const templateCopyKeys = {
  "monthly-calendar": "monthly",
  "daily-agenda": "daily",
  "weekly-timeline": "weekly",
  "stock-info": "stock",
  "news-card": "news",
  "world-clock": "clock",
  "weather-today": "weather",
  "daily-focus": "focus",
  "current-weather": "current",
  "weekly-weather": "weeklyWeather",
};

function getTemplateCopy(template, copy) {
  const key = templateCopyKeys[template.id];
  return { name: copy.names[key] || template.name, description: copy.descriptions[key] || template.description };
}

function getCategoryCopy(category, copy) {
  const categoryIndex = categories.findIndex((item) => item.label === category);
  return copy.categories[categoryIndex] || category;
}

function formatCanvas(template) {
  return `${template.canvas.width} × ${template.canvas.height} px`;
}

function TemplateArtwork({ template, large = false }) {
  const { t } = useLanguage();
  if (!template?.image) {
    return <div className="flex aspect-[5/3] items-center justify-center bg-slate-50 text-xs font-semibold text-slate-400">{t("templates.previewUnavailable")}</div>;
  }

  const aspectClass = template.device === "Mini" ? "aspect-[4/3]" : "aspect-[5/3]";
  return <img src={template.image} alt={t("accessibility.templatePreview").replace("{name}", getTemplateCopy(template, t("templates")).name)} loading={large ? "eager" : "lazy"} className={`block w-full ${aspectClass} object-contain ${large ? "max-h-[520px]" : ""}`} />;
}
